import React from 'react';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import clsx from 'clsx';
import { CustomPaging, PagingState } from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    TableHeaderRow
} from '@devexpress/dx-react-grid-material-ui';
import moment from 'moment';
import { getTimeZoneAbbr } from '../../helpers/timezones';

import CellRenderer from '../../components/RuleExceptionsTable/CellRenderer';
import MetaContext from '../../components/RuleExceptionsTable/MetaContext';
import NoDataCell from '../../components/RuleExceptionsTable/NoDataCell';

import defaultClasses from '../../components/RuleExceptionsTable/RuleExceptionsTable.module.css';

const COLUMNS = [
    {
        getCellValue(row) {
            if (!row.timestamp) {
                return;
            }
            const zone = moment.tz.guess();
            const timeDisplay = moment.parseZone(row.timestamp).local().format('YYYY-MM-DD h:mm A') + ' ' + getTimeZoneAbbr(row.timestamp, zone);
            return timeDisplay;
        },
        name:  'timestamp',
        title: 'Timestamp'
    },
    {
        getCellValue(row) {
            if (row.rawUnit) {
                return `${row.rawValue} (${row.rawUnit})`;
            }
            if (row.rawValue === null) {
                return 'No Data';
            }
            return row.rawValue;
        },
        name:  'rawValue',
        title: 'Raw Value'
    },
    {
        getCellValue(row) {
            if (typeof(row.convertedValue) === 'boolean' && row.presentationUnit) {
                return `${row.convertedValue.toString()} (${row.presentationUnit})`;
            }
            if (typeof(row.convertedValue) === 'boolean') {
                return `${row.convertedValue.toString()}`;
            }
            if (row.presentationUnit) {
                return `${row.convertedValue} (${row.presentationUnit})`;
            }
            if (row.convertedValue === null) {
                return 'No Data';
            }
            return `${row.convertedValue}`; 
        },
        name:  'convertedValue',
        title: 'Display Value'
    }
];

const StyledTable = (props) => (
    <Table.Table {...props} className={defaultClasses.table} />
);

const StyledHeaderCellComponent = (props) => (
    <TableHeaderRow.Cell
        {...props}
        className={clsx(defaultClasses.cell, defaultClasses.headerCell)}
    />
);

const TableColumnExtensions = [
    { columnName: 'timestamp', width: 250 }
];

const SensorDataTable = ({
    currentPage,
    error,
    sensorData,
    isLoading,
    onCurrentPageChange,
    onPageSizeChange,
    pageSize,
    totalCount
}) => {
    let noDataMessage = 'There is no Sensor data for this time period';
    if (isLoading) {
        noDataMessage = 'Loading Sensor Data...';
    } else if (error) {
        noDataMessage = `Error Loading Sensor Data: ${error}`;
    }

    return (
        <MetaContext.Provider value={{ error }}>
            <Grid columns={COLUMNS} rows={sensorData}>
                <PagingState
                    currentPage={currentPage}
                    onCurrentPageChange={onCurrentPageChange}
                    onPageSizeChange={onPageSizeChange}
                    pageSize={pageSize}
                />
                <CustomPaging totalCount={totalCount} />
                <Table
                    cellComponent={CellRenderer}
                    columnExtensions={TableColumnExtensions}
                    messages={{
                        noData: noDataMessage
                    }}
                    noDataCellComponent={NoDataCell}
                    tableComponent={StyledTable}
                />
                <TableHeaderRow cellComponent={StyledHeaderCellComponent} />
            </Grid>
        </MetaContext.Provider>
    );
};

SensorDataTable.defaultProps = {
    sensorData:          [],
    isLoading:           false,
    onCurrentPageChange: () => {},
    onPageSizeChange:    () => {}
};

SensorDataTable.propTypes = {
    currentPage: number,
    error:       string,
    sensorData:  arrayOf(shape({
        insertTimestamp: string,
        notes:           string,
        ruleName:        string
    })),
    isLoading:           bool,
    onCurrentPageChange: func,
    onPageSizeChange:    func,
    pageSize:            number,
    totalCount:          number
};

export default SensorDataTable;
